import React, { useMemo, useState } from "react";
import styled from "styled-components";

import {
  Button,
  Card,
  SectionHeader,
  Skeleton,
  Tag,
  TagVariant,
} from "../../../../design-system";
import { OutreachAnalysisFinding } from "../../../../types/OutreachMetrics";
import { AnalysisData } from "./analysisData";
import { QueueModel } from "./queueModel";
import { CHANNEL_COLOR } from "./RailCapacity";

// One queue, one count, two tiers.
//
// "Who needs a human" used to be five numbers computed five ways: a tile on the
// analysis tab, a lane on the ops tab, the Today badge, the Review badge, and
// the number the daily routine writes into the rail snapshot. They disagreed.
// Now there is one list.
//
// Tier 1 is judgement work: findings, drafts waiting on a decision, and the
// routine's own notes. About twenty rows.
// Tier 2 is throughput work: the follow-ups owed on leads. Today that is a
// backlog in the hundreds, so it is one row that expands. A hundred routine
// follow-ups must not sit above four findings about a dead send rail.

const PRIORITY_VARIANT: Record<string, TagVariant> = {
  P0: "danger",
  P1: "warning",
  P2: "neutral",
};

const TierHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: var(--space-3);
  margin-top: var(--space-5);
  padding-bottom: var(--space-2);
  font-family: var(--font-sans);
  flex-wrap: wrap;
`;

const TierTitle = styled.span`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const TierHint = styled.span`
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
`;

const Rows = styled.div`
  border-top: 1px solid var(--color-border-subtle);
`;

const Row = styled.div`
  border-bottom: 1px solid var(--color-border-subtle);
`;

const RowHead = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  padding: var(--space-3) 0;
`;

const Grow = styled.button`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: baseline;
  gap: var(--space-3);
  flex-wrap: wrap;
  text-align: left;
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
  font-family: var(--font-sans);
  color: inherit;
`;

const RowTitle = styled.span`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text);
  flex: 1 1 260px;
  min-width: 0;
  line-height: var(--line-height-snug);
`;

const RowMeta = styled.span`
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  white-space: nowrap;
`;

const Detail = styled.div`
  padding: 0 0 var(--space-4);
  max-width: 78ch;
`;

const Body = styled.p`
  margin: 0;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  line-height: 1.55;
  color: var(--color-text-secondary);
`;

const FixLine = styled.p`
  margin: var(--space-3) 0 0;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  line-height: 1.55;
  color: var(--color-text);
`;

const FixLabel = styled.span`
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-text-tertiary);
  margin-right: var(--space-2);
`;

const RailRef = styled.span`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  margin-top: var(--space-3);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
`;

const Swatch = styled.span<{ $color: string }>`
  width: 9px;
  height: 9px;
  border-radius: 3px;
  flex: none;
  background: ${(p) => p.$color};
`;

const Note = styled.p`
  margin: var(--space-4) 0 0;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  line-height: 1.55;
  color: var(--color-text-secondary);
`;

const ErrorNote = styled(Note)`
  color: var(--color-danger);
`;

const Rest = styled.p`
  margin: var(--space-4) 0 0;
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
`;

const LeadWrap = styled.div`
  padding: var(--space-2) 0 var(--space-4);
`;

const HeadRight = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
`;

// Reopening is rare, so it sits under the queue rather than above it. It has to
// exist: Done writes to Firestore and a misclick would otherwise be permanent.
const ClosedBar = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  margin-top: var(--space-4);
  padding: var(--space-2) 0;
  border: 0;
  background: none;
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);

  &:hover {
    color: var(--color-text-secondary);
  }
`;

const Total = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  font-variant-numeric: tabular-nums;
  color: var(--color-text);
`;

// THE work count. The tab badge and the queue heading both call this, so they
// cannot drift the way the five counts this queue replaced did.
export const workQueueTotal = (
  analysis: AnalysisData,
  queue: QueueModel,
  leadsReady: boolean
): number =>
  analysis.openFindings.length +
  (queue.drafts.needsDecision > 0 ? 1 : 0) +
  (analysis.railDay?.humanQueue?.items?.length ?? 0) +
  (leadsReady ? queue.leadWork : 0);

export type WorkQueueProps = {
  analysis: AnalysisData;
  queue: QueueModel;
  // False while the lead list is still in flight. The judgement rows paint
  // first; only the follow-up tier waits.
  leadsReady: boolean;
  onOpenDrafts: () => void;
  // Owned by the screen, because the lead rows and their drawer live there.
  leadSection: React.ReactNode;
  // Reopen a finding closed by mistake. Marking one Done writes to Firestore,
  // so it must not be a one-way door.
  closedFindings: OutreachAnalysisFinding[];
};

const WorkQueue: React.FC<WorkQueueProps> = ({
  analysis,
  queue,
  leadsReady,
  onOpenDrafts,
  leadSection,
  closedFindings,
}) => {
  const {
    openFindings,
    history,
    current,
    railDay,
    isLatestDay,
    latestDate,
    saving,
    actionError,
    setStatus,
  } = analysis;

  // P0 rows open on arrival. Everything else is a title until asked for.
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [showLeads, setShowLeads] = useState(false);
  const [showClosed, setShowClosed] = useState(false);

  const notes = railDay?.humanQueue?.items ?? [];
  const draftWork = queue.drafts.needsDecision;

  const judgement = openFindings.length + (draftWork > 0 ? 1 : 0) + notes.length;
  const total = workQueueTotal(analysis, queue, leadsReady);
  // The analysis fetch and the lead fetch land independently. Until BOTH have,
  // an empty queue means "not here yet", not "nothing to do".
  const ready = leadsReady && analysis.analyses !== null;

  const isOpen = (id: string, fallback: boolean) =>
    expanded[id] === undefined ? fallback : expanded[id];

  const toggle = (id: string, fallback: boolean) =>
    setExpanded((e) => ({ ...e, [id]: !isOpen(id, fallback) }));

  const p0 = openFindings.filter((f) => f.priority === "P0").length;

  const statusHint = isLatestDay
    ? undefined
    : `Status is set on the newest analysis${
        latestDate ? ` (${latestDate})` : ""
      }.`;

  const findingRow = (finding: OutreachAnalysisFinding, closed = false) => {
    const open = isOpen(finding.id, !closed && finding.priority === "P0");
    const seen = history.get(finding.id);
    return (
      <Row key={finding.id}>
        <RowHead>
          <Tag
            variant={PRIORITY_VARIANT[finding.priority] ?? "neutral"}
            size="sm"
          >
            {finding.priority}
          </Tag>
          <Grow
            type="button"
            aria-expanded={open}
            onClick={() =>
              toggle(finding.id, !closed && finding.priority === "P0")
            }
          >
            <RowTitle>{finding.title}</RowTitle>
            <RowMeta>
              {closed
                ? `Closed${seen?.firstClosed ? ` ${seen.firstClosed}` : ""}`
                : `Open since ${seen?.firstSeen ?? current?.date ?? ""}`}
            </RowMeta>
          </Grow>
          <Button
            variant="ghost"
            size="sm"
            disabled={saving === finding.id || !isLatestDay}
            title={statusHint}
            onClick={() => setStatus(finding, closed ? "open" : "done")}
          >
            {closed ? "Reopen" : "Done"}
          </Button>
        </RowHead>
        {open && (
          <Detail>
            <Body>{finding.body}</Body>
            <FixLine>
              <FixLabel>Fix</FixLabel>
              {finding.fix}
            </FixLine>
            {finding.railId && (
              <RailRef>
                <Swatch
                  $color={
                    CHANNEL_COLOR[finding.railId.split(":")[0]] ??
                    "var(--color-text-tertiary)"
                  }
                />
                {finding.railId}
              </RailRef>
            )}
          </Detail>
        )}
      </Row>
    );
  };

  const ordered = useMemo(() => {
    const byPriority = (p: string) =>
      openFindings.filter((f) => f.priority === p);
    return {
      first: byPriority("P0"),
      rest: [...byPriority("P1"), ...byPriority("P2")],
    };
  }, [openFindings]);

  const nothingToDo = total === 0 && ready;

  return (
    <Card padding="lg" elevation="md">
      <SectionHeader
        title="Needs you"
        description="Everything waiting on a person, ranked. Work down the list."
        actions={
          <HeadRight>
            <Button variant="ghost" size="sm" onClick={onOpenDrafts}>
              Drafts
            </Button>
            <Total>{total}</Total>
          </HeadRight>
        }
      />

      {actionError && <ErrorNote>{actionError}</ErrorNote>}
      {statusHint && <Note>{statusHint}</Note>}

      {nothingToDo && (
        <Note>
          Nothing is waiting on you. The rails below say whether the automation
          is still moving.
        </Note>
      )}

      {judgement > 0 && (
        <>
          <TierHead>
            <TierTitle>Decisions</TierTitle>
            <TierHint>
              {p0 > 0
                ? `${p0} at P0. These cost sends or replies every day they stay open.`
                : "Judgement calls from the day's analysis."}
            </TierHint>
          </TierHead>
          <Rows>
            {ordered.first.map((f) => findingRow(f))}

            {draftWork > 0 && (
              <Row>
                <RowHead>
                  <Tag
                    variant={queue.drafts.errors > 0 ? "danger" : "accent"}
                    size="sm"
                  >
                    Drafts
                  </Tag>
                  <Grow type="button" onClick={onOpenDrafts}>
                    <RowTitle>
                      {draftWork} {draftWork === 1 ? "draft" : "drafts"} waiting
                      on a decision
                    </RowTitle>
                    <RowMeta>
                      {queue.drafts.pending} pending · {queue.drafts.held} held ·{" "}
                      {queue.drafts.errors} failed
                    </RowMeta>
                  </Grow>
                  <Button variant="ghost" size="sm" onClick={onOpenDrafts}>
                    Review
                  </Button>
                </RowHead>
              </Row>
            )}

            {notes.map((note, i) => (
              <Row key={`note-${i}`}>
                <RowHead>
                  <Tag variant="neutral" size="sm">
                    Note
                  </Tag>
                  <Grow as="div">
                    <RowTitle>{note}</RowTitle>
                    <RowMeta>
                      From the {railDay?.date ?? ""} run
                      {typeof railDay?.humanQueue?.oldestDays === "number"
                        ? ` · oldest ${railDay.humanQueue.oldestDays}d`
                        : ""}
                    </RowMeta>
                  </Grow>
                </RowHead>
              </Row>
            ))}

            {ordered.rest.map((f) => findingRow(f))}
          </Rows>
        </>
      )}

      <TierHead>
        <TierTitle>Follow-ups</TierTitle>
        <TierHint>Leads where you owe the next message.</TierHint>
      </TierHead>
      <Rows>
        <Row>
          {!leadsReady ? (
            <RowHead>
              <Skeleton width="240px" height={16} />
            </RowHead>
          ) : queue.leadWork === 0 ? (
            <RowHead>
              <Grow as="div">
                <RowTitle>Nothing is due.</RowTitle>
              </Grow>
            </RowHead>
          ) : (
            <>
              <RowHead>
                <Tag
                  variant={queue.overdue.length > 0 ? "danger" : "accent"}
                  size="sm"
                >
                  {queue.leadWork}
                </Tag>
                <Grow
                  type="button"
                  aria-expanded={showLeads}
                  onClick={() => setShowLeads((s) => !s)}
                >
                  <RowTitle>
                    {queue.overdue.length} overdue, {queue.dueToday.length} due
                    today
                  </RowTitle>
                  <RowMeta>{showLeads ? "Hide" : "Show the list"}</RowMeta>
                </Grow>
              </RowHead>
              {showLeads && <LeadWrap>{leadSection}</LeadWrap>}
            </>
          )}
        </Row>
      </Rows>

      {closedFindings.length > 0 && (
        <ClosedBar
          type="button"
          aria-expanded={showClosed}
          onClick={() => setShowClosed((v) => !v)}
        >
          {showClosed ? "Hide" : "Show"} {closedFindings.length} closed{" "}
          {closedFindings.length === 1 ? "finding" : "findings"}
        </ClosedBar>
      )}
      {showClosed && <Rows>{closedFindings.map((f) => findingRow(f, true))}</Rows>}

      {leadsReady && (
        <Rest>
          {queue.noAction.toLocaleString()} leads have no next action.{" "}
          {queue.inAutomation.toLocaleString()} more are with the automation.{" "}
          {queue.awaitingReply.toLocaleString()} are waiting on a reply.
        </Rest>
      )}
    </Card>
  );
};

export default WorkQueue;
